import type React from 'react';

import type { BrokerStatus, P2PDiagnostics } from '@/types/P2PDiagnostics';

interface P2PTabProps {
  p2pDiagnostics: P2PDiagnostics | null;
}

const BROKER_STATUS_CLASS: Record<BrokerStatus, string> = {
  connected: 'text-emerald-600 dark:text-emerald-400',
  disconnected: 'text-red-600 dark:text-red-400',
  unknown: 'text-muted-foreground',
};

export default function P2PTab({ p2pDiagnostics }: P2PTabProps): React.JSX.Element {
  if (p2pDiagnostics === null) {
    return <p className="text-sm text-muted-foreground">P2P stack not initialized.</p>;
  }

  const { peerCount, peers, multiaddrs, pinCount, pinnedCids, brokerStatus } = p2pDiagnostics;

  return (
    <div className="flex flex-col gap-4 text-sm">
      <dl className="space-y-2">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Peers</dt>
          <dd className="font-medium text-foreground">{peerCount}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Helia pins</dt>
          <dd className="font-medium text-foreground">{pinCount ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Broker</dt>
          <dd className={`font-medium ${BROKER_STATUS_CLASS[brokerStatus]}`}>{brokerStatus}</dd>
        </div>
      </dl>

      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
          Helia files (pinned)
        </h3>
        {pinnedCids.length === 0 ? (
          <p className="text-xs text-muted-foreground">No pinned CIDs.</p>
        ) : (
          <ul className="space-y-1 font-mono text-xs">
            {pinnedCids.map((cid) => (
              <li key={cid} className="break-all text-foreground">
                {cid}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
          Node multiaddrs
        </h3>
        {multiaddrs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No advertised addresses.</p>
        ) : (
          <ul className="space-y-1 font-mono text-xs">
            {multiaddrs.map((address) => (
              <li key={address} className="break-all text-foreground">
                {address}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
          Connected peers
        </h3>
        {peers.length === 0 ? (
          <p className="text-xs text-muted-foreground">No connected peers.</p>
        ) : (
          <ul className="space-y-2">
            {peers.map((peer, index) => (
              <li
                key={`${peer.peerId}-${peer.address}-${String(index)}`}
                className="rounded border border-border bg-muted/20 p-2 font-mono text-xs"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-all font-semibold text-foreground">{peer.peerId}</span>
                  <span className="text-muted-foreground">{peer.direction}</span>
                  {peer.isDirect && (
                    <span className="text-emerald-600 dark:text-emerald-400">direct</span>
                  )}
                  {peer.isRelay && (
                    <span className="text-amber-600 dark:text-amber-400">relay</span>
                  )}
                </div>
                <div className="mt-1 break-all text-muted-foreground">{peer.address}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
